#!/usr/bin/env node

import { SeacloudClient } from './core/index.js';
import { initSeacloud } from './core/global-config.js';
import { llmChatCompletions } from './api/llm_chat_completions.js';
import { agentChatCompletions, createTextMessage } from './api/agent_chat_completions.js';
import { appSearch } from './api/app_search.js';
import { appGeneration } from './api/app_generation.js';
import { scan } from './api/scan.js';
import { readFileSync } from 'fs';
import { fileURLToPath } from 'url';
import { dirname, join } from 'path';

const __filename = fileURLToPath(import.meta.url);
const __dirname = dirname(__filename);

interface CliOptions {
  apiKey?: string;
  baseUrl?: string;
  model?: string;
  params?: Record<string, any>;
}

function showHelp() {
  console.log(`
SeaCloud CLI - Test AI models, LLM, and Agent from command line

Usage:
  seacloud <command> [options]

Commands:
  llm <prompt>         Chat with LLM models
  agent <prompt>       Chat with Fast Agent (supports image/video generation)
  app <subcommand>     App-related operations (search, generation)
  scan <uri>           Content safety scan (image/video/audio)
  status <task-id>     Query task status by task ID
  <model>              Test specific model generation

App Subcommands:
  app search <ids>     Query template specifications by template IDs
  app generation       Create generation task from template

LLM Options:
  --model <name>       Model name (default: seaart-mix-sonnet-4-5)
  --stream             Enable streaming mode
  --temperature <n>    Temperature 0-2 (default: 0.7)
  --max-tokens <n>     Max tokens to generate

Agent Options:
  --agent-id <id>      Agent ID (default: seagen_agent)
  --model <name>       Model name (default: gpt-4o)
  --stream             Enable streaming mode
  --session-id <id>    Session ID for multi-turn conversation

App Search Options:
  --type <type>        Template type (default: comfyui)

App Generation Options:
  --template-id <id>   Template ID (required)
  --params <json>      Input parameters as JSON string (required)

Scan Options:
  --risk-types <types> Risk types (default: POLITY,EROTIC,VIOLENT,CHILD)
  --detected-age       Enable age detection (default: false)
  --api-key <key>      API key (or set API_SERVICE_TOKEN env var)
  --base-url <url>     Base URL (default: https://seaspark-seacloud-proxy-rs.dev.seaspark.ai)

Model Generation Options:
  --api-key <key>      API key (or set API_SERVICE_TOKEN env var)
  --base-url <url>     Base URL (default: https://seaspark-seacloud-proxy-rs.dev.seaspark.ai)
  --params <json>      JSON parameters for the model

Status Query Options:
  --api-key <key>      API key (or set API_SERVICE_TOKEN env var)
  --base-url <url>     Base URL (default: https://seaspark-seacloud-proxy-rs.dev.seaspark.ai)

Examples:
  # Chat with LLM (non-streaming)
  seacloud llm "What is the capital of France?"

  # Chat with LLM (streaming)
  seacloud llm "Tell me a story" --stream

  # Chat with LLM using specific model
  seacloud llm "Hello" --model deepseek-v3.1 --temperature 1.0

  # Chat with Agent (can generate images)
  seacloud agent "Generate an image of a sunset"

  # Chat with Agent (streaming)
  seacloud agent "Create a cat image" --stream

  # Query template specifications (app search)
  seacloud app search "template-id-1,template-id-2"
  seacloud app search "template-abc" --type comfyui

  # Create generation task (app generation)
  seacloud app generation --template-id "d26trpte878eqsnm3bjg" --params '[{"field":"prompt1","value":"hello"}]'

  # Query task status
  seacloud status d123456789abcdef

  # Scan image
  seacloud scan "https://example.com/image.jpg"

  # Scan video with custom risk types
  seacloud scan "https://example.com/video.mp4" --risk-types EROTIC,VIOLENT

  # Scan with age detection
  seacloud scan "https://example.com/photo.jpg" --detected-age

  # Test model generation
  seacloud flux_1_1_pro --params '{"prompt":"a beautiful sunset"}'

  # Use custom API key
  seacloud llm "Hello" --api-key sa-xxx

Environment Variables:
  API_SERVICE_TOKEN    API authentication token (required)
  SEACLOUD_BASE_URL    Base URL for API endpoints
`);
}

function parseArgs(args: string[]): CliOptions {
  const options: CliOptions = {};

  for (let i = 0; i < args.length; i++) {
    const arg = args[i];

    switch (arg) {
      case '--api-key':
        options.apiKey = args[++i];
        break;
      case '--base-url':
        options.baseUrl = args[++i];
        break;
      case '--model':
        options.model = args[++i];
        break;
      case '--params':
        try {
          options.params = JSON.parse(args[++i]);
        } catch (e) {
          console.error('Error: Invalid JSON for --params');
          process.exit(1);
        }
        break;
    }
  }

  return options;
}

async function testModel(model: string, options: CliOptions) {
  const apiKey = options.apiKey || process.env.API_SERVICE_TOKEN || '';
  const baseUrl = options.baseUrl || process.env.SEACLOUD_BASE_URL || 'https://seaspark-seacloud-proxy-rs.dev.seaspark.ai';

  if (!options.params) {
    console.error('Error: --params required. Provide JSON parameters for the model');
    process.exit(1);
  }

  console.log(`Testing model: ${model}`);
  console.log(`Base URL: ${baseUrl}`);
  console.log(`Parameters:`, JSON.stringify(options.params, null, 2));
  console.log('');

  const client = await SeacloudClient.create({ apiKey, baseUrl });

  try {
    console.log('Creating task...');
    const task = await client.createTask('/model/v1/generation', {
      model,
      input: [{ params: options.params }],
    });

    console.log(`Task created: ${task.id}`);
    console.log(`Initial status: ${task.status}`);
    console.log('');

    if (task.status === 'failed') {
      console.error('Task failed immediately:', task.error);
      process.exit(1);
    }

    console.log('Polling for results...');
    let attempt = 0;
    const maxAttempts = 120;
    const intervalMs = 3000;

    while (attempt < maxAttempts) {
      attempt++;

      const result = await client.getTaskStatus('/model/v1/generation', task.id);
      process.stdout.write(`\rAttempt ${attempt}/${maxAttempts} - Status: ${result.status}    `);

      if (result.status === 'completed') {
        console.log('\n\nTask completed!');
        console.log('\nResults:');
        console.log(JSON.stringify(result.output, null, 2));

        // Extract URLs if available
        if (result.output) {
          const urls: string[] = [];
          for (const item of result.output) {
            if (item.content) {
              for (const resource of item.content) {
                if (resource.url) {
                  urls.push(resource.url);
                }
              }
            }
          }

          if (urls.length > 0) {
            console.log('\nGenerated URLs:');
            urls.forEach((url, i) => {
              console.log(`  ${i + 1}. ${url}`);
            });
          }
        }

        process.exit(0);
      }

      if (result.status === 'failed') {
        console.log('\n\nTask failed!');
        console.error('Error:', result.error);
        process.exit(1);
      }

      await new Promise(resolve => setTimeout(resolve, intervalMs));
    }

    console.log('\n\nTimeout: Task did not complete within the time limit');
    process.exit(1);

  } catch (error: any) {
    console.error('\nError:', error.message);
    process.exit(1);
  }
}

async function runLlm(prompt: string, args: string[]) {
  const options: any = { stream: false, model: 'seaart-mix-sonnet-4-5', temperature: 0.7 };

  for (let i = 0; i < args.length; i++) {
    const arg = args[i];
    if (arg === '--model') options.model = args[++i];
    else if (arg === '--stream') options.stream = true;
    else if (arg === '--temperature') options.temperature = parseFloat(args[++i]);
    else if (arg === '--max-tokens') options.maxTokens = parseInt(args[++i]);
    else if (arg === '--api-key') options.apiKey = args[++i];
    else if (arg === '--base-url') options.baseUrl = args[++i];
  }

  const apiKey = options.apiKey || process.env.API_SERVICE_TOKEN || '';
  const baseUrl = options.baseUrl || process.env.SEACLOUD_BASE_URL || 'https://seaspark-seacloud-proxy-rs.dev.seaspark.ai';

  initSeacloud(apiKey, { baseUrl, timeout: 120000 });

  console.log(`Model: ${options.model}`);
  console.log(`Prompt: ${prompt}\n`);

  if (options.stream) {
    const stream = await llmChatCompletions({
      model: options.model,
      messages: [{ role: 'user', content: prompt }],
      stream: true,
      temperature: options.temperature,
      max_tokens: options.maxTokens,
    });

    process.stdout.write('Response: ');
    for await (const chunk of stream) {
      const content = chunk.choices[0]?.delta?.content;
      if (typeof content === 'string') process.stdout.write(content);
    }
    console.log('\n');
  } else {
    const response = await llmChatCompletions({
      model: options.model,
      messages: [{ role: 'user', content: prompt }],
      stream: false,
      temperature: options.temperature,
      max_tokens: options.maxTokens,
    });

    console.log('Response:', response.choices[0].message.content);
    console.log('\nUsage:', response.usage);
  }
}

async function runAgent(prompt: string, args: string[]) {
  const options: any = {
    stream: false,
    model: 'gpt-4o',
    agentId: 'seagen_agent',
  };

  for (let i = 0; i < args.length; i++) {
    const arg = args[i];
    if (arg === '--model') options.model = args[++i];
    else if (arg === '--stream') options.stream = true;
    else if (arg === '--agent-id') options.agentId = args[++i];
    else if (arg === '--session-id') options.sessionId = args[++i];
    else if (arg === '--api-key') options.apiKey = args[++i];
    else if (arg === '--base-url') options.baseUrl = args[++i];
  }

  const apiKey = options.apiKey || process.env.API_SERVICE_TOKEN || '';
  const baseUrl = options.baseUrl || process.env.SEACLOUD_BASE_URL || 'https://seaspark-seacloud-proxy-rs.dev.seaspark.ai';

  initSeacloud(apiKey, { baseUrl, timeout: 300000 }); // 5 min for agent

  console.log(`Agent: ${options.agentId}`);
  console.log(`Model: ${options.model}`);
  console.log(`Prompt: ${prompt}\n`);

  if (options.stream) {
    const stream = await agentChatCompletions({
      agent_id: options.agentId,
      messages: [createTextMessage('user', prompt)],
      model: options.model,
      stream: true,
      session_id: options.sessionId,
      seq: 0,
    });

    process.stdout.write('Response: ');
    for await (const chunk of stream) {
      const content = chunk.choices[0]?.delta?.content;
      if (typeof content === 'string') process.stdout.write(content);
    }
    console.log('\n');
  } else {
    const response = await agentChatCompletions({
      agent_id: options.agentId,
      messages: [createTextMessage('user', prompt)],
      model: options.model,
      stream: false,
      session_id: options.sessionId,
      seq: 0,
    });

    console.log('Response:', response.choices[0].message.content);

    if (response.artifacts && response.artifacts.length > 0) {
      console.log('\nGenerated Artifacts:');
      response.artifacts.forEach((artifact, i) => {
        console.log(`  ${i + 1}. ${artifact.name}`);
        console.log(`     URL: ${artifact.url}`);
      });
    }

    console.log('\nSession ID:', response.session_id);
    console.log('Message ID:', response.msg_id);
  }
}

async function runAppGeneration(args: string[]) {
  const options: any = {};

  for (let i = 0; i < args.length; i++) {
    const arg = args[i];
    if (arg === '--template-id') options.templateId = args[++i];
    else if (arg === '--params') {
      try {
        options.params = JSON.parse(args[++i]);
      } catch (e) {
        console.error('Error: --params must be valid JSON array');
        console.error('Example: --params \'[{"field":"prompt1","value":"hello"}]\'');
        process.exit(1);
      }
    }
    else if (arg === '--api-key') options.apiKey = args[++i];
    else if (arg === '--base-url') options.baseUrl = args[++i];
  }

  if (!options.templateId) {
    console.error('Error: --template-id required');
    console.error('Usage: seacloud app generation --template-id <id> --params <json>');
    process.exit(1);
  }

  if (!options.params) {
    console.error('Error: --params required');
    console.error('Usage: seacloud app generation --template-id <id> --params <json>');
    console.error('Example: --params \'[{"field":"prompt1","value":"hello"}]\'');
    process.exit(1);
  }

  const apiKey = options.apiKey || process.env.API_SERVICE_TOKEN || '';
  const baseUrl = options.baseUrl || process.env.SEACLOUD_BASE_URL || 'https://seaspark-seacloud-proxy-rs.dev.seaspark.ai';

  const client = await SeacloudClient.create({ apiKey, baseUrl });

  try {
    console.log('Creating task...');
    const task = await client.createTask('/model/v1/generation', {
      model: 'comfyui',
      input: [{ params: { template_id: options.templateId, inputs: options.params } }],
    });

    console.log(`Task created: ${task.id}`);
    console.log(`Initial status: ${task.status}`);
    console.log('');

    if (task.status === 'failed') {
      console.error('Task failed immediately:', task.error);
      process.exit(1);
    }

    console.log('Polling for results...');
    let attempt = 0;
    const maxAttempts = 120;
    const intervalMs = 3000;

    while (attempt < maxAttempts) {
      attempt++;

      const result = await client.getTaskStatus('/model/v1/generation', task.id);
      process.stdout.write(`\rAttempt ${attempt}/${maxAttempts} - Status: ${result.status}    `);

      if (result.status === 'completed') {
        console.log('\n\nTask completed!');
        console.log('\nResults:');
        console.log(JSON.stringify(result, null, 2));

        process.exit(0);
      }

      if (result.status === 'failed') {
        console.log('\n\nTask failed!');
        console.error('Error:', result.error);
        process.exit(1);
      }

      await new Promise(resolve => setTimeout(resolve, intervalMs));
    }

    console.log('\n\nTimeout: Task did not complete within the time limit');
    process.exit(1);

  } catch (error: any) {
    console.error('\nError:', error.message);
    process.exit(1);
  }
}

async function runAppSearch(templateIdsStr: string, args: string[]) {
  const options: any = { type: 'comfyui' };

  for (let i = 0; i < args.length; i++) {
    const arg = args[i];
    if (arg === '--type') options.type = args[++i];
    else if (arg === '--api-key') options.apiKey = args[++i];
    else if (arg === '--base-url') options.baseUrl = args[++i];
  }

  const apiKey = options.apiKey || process.env.API_SERVICE_TOKEN || '';
  const baseUrl = options.baseUrl || process.env.SEACLOUD_BASE_URL || 'https://seaspark-seacloud-proxy-rs.dev.seaspark.ai';

  initSeacloud(apiKey, { baseUrl });

  const templateIds = templateIdsStr.split(',').map(id => id.trim());

  const result = await appSearch({
    type: options.type,
    template_ids: templateIds,
  });

  console.log(JSON.stringify(result, null, 2));
}

/**
 * 检测 URI 是否为视频或音频
 */
function detectMediaType(uri: string): { isVideo: number; mediaType: string } {
  // 视频扩展名正则（不区分大小写）
  const videoExtRegex = /\.(?:mp4|avi|mkv|mov|flv|wmv|webm|m4v|3gp|ts|mts|mpeg|mpg|vob|ogv|m2ts|divx|rm|rmvb|asf|f4v)(?:[^a-z0-9]|$)/i;

  // 音频扩展名正则（不区分大小写）
  const audioExtRegex = /\.(?:mp3|wav|ogg|m4a|aac|flac|wma|m4b|m4p|m4r)(?:[^a-z0-9]|$)/i;

  if (videoExtRegex.test(uri)) {
    return { isVideo: 1, mediaType: 'video' };
  }

  if (audioExtRegex.test(uri)) {
    return { isVideo: 1, mediaType: 'audio' };
  }

  return { isVideo: 0, mediaType: 'image' };
}

async function runScan(uri: string, args: string[]) {
  const options: any = {
    riskTypes: ['POLITY', 'EROTIC', 'VIOLENT', 'CHILD'],
    detectedAge: false,
  };

  for (let i = 0; i < args.length; i++) {
    const arg = args[i];
    if (arg === '--risk-types') {
      options.riskTypes = args[++i].split(',').map((t: string) => t.trim());
    } else if (arg === '--detected-age') {
      options.detectedAge = true;
    } else if (arg === '--api-key') {
      options.apiKey = args[++i];
    } else if (arg === '--base-url') {
      options.baseUrl = args[++i];
    }
  }

  const apiKey = options.apiKey || process.env.API_SERVICE_TOKEN || '';
  const baseUrl = options.baseUrl || process.env.SEACLOUD_BASE_URL || 'https://seaspark-seacloud-proxy-rs.dev.seaspark.ai';

  initSeacloud(apiKey, { baseUrl });

  // 自动检测媒体类型
  const { isVideo, mediaType } = detectMediaType(uri);

  console.log(`资源地址: ${uri}`);
  console.log(`媒体类型: ${mediaType}`);
  console.log(`风险类型: ${options.riskTypes.join(', ')}`);
  console.log(`年龄检测: ${options.detectedAge ? '启用' : '禁用'}`);
  console.log('');

  try {
    const result = await scan({
      uri,
      risk_types: options.riskTypes,
      detected_age: options.detectedAge ? 1 : 0,
      is_video: isVideo,
    });

    console.log('✅ 扫描完成!\n');
    console.log(`请求状态: ${result.ok ? '成功' : '失败'}`);

    if (result.nsfw_level !== undefined) {
      console.log(`风险级别: ${result.nsfw_level} (0-6)`);
    }

    if (result.risk_types && result.risk_types.length > 0) {
      console.log(`检测到的风险类型: ${result.risk_types.join(', ')}`);
    }

    if (result.age_group && result.age_group.length > 0) {
      console.log(`年龄组: ${JSON.stringify(result.age_group)}`);
    }

    // 视频特有字段
    if (isVideo === 1) {
      if (result.video_duration !== undefined) {
        console.log(`视频时长: ${result.video_duration} 秒`);
      }
      if (result.frame_count !== undefined) {
        console.log(`总抽帧数: ${result.frame_count}`);
      }
      if (result.frames_checked !== undefined) {
        console.log(`实际检测帧数: ${result.frames_checked}`);
      }
      if (result.max_risk_frame !== undefined) {
        console.log(`最高风险帧索引: ${result.max_risk_frame}`);
      }
      if (result.early_exit !== undefined) {
        console.log(`提前退出: ${result.early_exit ? '是' : '否'}`);
      }
    }

    // 显示标签详情
    if (result.label_items && result.label_items.length > 0) {
      console.log(`\n检测到的标签 (${result.label_items.length} 个):`);
      result.label_items.forEach((item, i) => {
        console.log(`  ${i + 1}. ${item.name}`);
        console.log(`     风险级别: ${item.score}, 类型: ${item.risk_type}`);
      });
    }

    // 显示帧检测结果（前5帧）
    if (result.frame_results && result.frame_results.length > 0) {
      console.log(`\n帧检测结果 (前5帧):`);
      result.frame_results.slice(0, 5).forEach((frame) => {
        console.log(`  帧 ${frame.frame_index}: 风险级别 ${frame.nsfw_level}, 标签数 ${frame.label_items.length}`);
      });
      if (result.frame_results.length > 5) {
        console.log(`  ... 还有 ${result.frame_results.length - 5} 帧结果`);
      }
    }

    if (result.error) {
      console.log(`\n❌ 错误: ${result.error}`);
    }

  } catch (error: any) {
    console.error('\n❌ 扫描失败:', error.message);
    process.exit(1);
  }
}

async function runTaskStatus(taskId: string, args: string[]) {
  const options: any = {};

  for (let i = 0; i < args.length; i++) {
    const arg = args[i];
    if (arg === '--api-key') options.apiKey = args[++i];
    else if (arg === '--base-url') options.baseUrl = args[++i];
  }

  const apiKey = options.apiKey || process.env.API_SERVICE_TOKEN || '';
  const baseUrl = options.baseUrl || process.env.SEACLOUD_BASE_URL || 'https://seaspark-seacloud-proxy-rs.dev.seaspark.ai';

  const client = await SeacloudClient.create({ apiKey, baseUrl });

  console.log(`Querying task status...`);
  console.log(`Task ID: ${taskId}`);
  console.log(`Base URL: ${baseUrl}\n`);

  try {
    const result = await client.getTaskStatus('/model/v1/generation', taskId);

    console.log(`Status: ${result.status}`);
    console.log(`Task ID: ${result.id}\n`);

    if (result.status === 'completed') {
      console.log('✅ Task completed successfully!\n');
      console.log('Output:');
      console.log(JSON.stringify(result.output, null, 2));

      // Extract URLs if available
      if (result.output) {
        const urls: string[] = [];
        for (const item of result.output) {
          if (item.content) {
            for (const resource of item.content) {
              if (resource.url) {
                urls.push(resource.url);
              }
            }
          }
        }

        if (urls.length > 0) {
          console.log('\nGenerated URLs:');
          urls.forEach((url, i) => {
            console.log(`  ${i + 1}. ${url}`);
          });
        }
      }
    } else if (result.status === 'failed') {
      console.log('❌ Task failed!\n');
      console.log('Error:', JSON.stringify(result.error, null, 2));
    } else if (result.status === 'processing') {
      console.log('⏳ Task is still processing...');
      console.log('Please check again later.');
    } else if (result.status === 'pending') {
      console.log('⏳ Task is pending...');
      console.log('Please check again later.');
    } else {
      console.log('Full result:');
      console.log(JSON.stringify(result, null, 2));
    }

  } catch (error: any) {
    console.error('\nError querying task status:', error.message);
    if (error.statusCode) {
      console.error('HTTP Status Code:', error.statusCode);
    }
    process.exit(1);
  }
}

async function main() {
  const args = process.argv.slice(2);

  if (args.length === 0 || args[0] === '--help' || args[0] === '-h') {
    showHelp();
    process.exit(0);
  }

  const command = args[0];

  try {
    if (command === 'llm') {
      if (args.length < 2) {
        console.error('Error: prompt required for llm command');
        console.log('Usage: seacloud llm "<prompt>" [options]');
        process.exit(1);
      }
      await runLlm(args[1], args.slice(2));
    } else if (command === 'agent') {
      if (args.length < 2) {
        console.error('Error: prompt required for agent command');
        console.log('Usage: seacloud agent "<prompt>" [options]');
        process.exit(1);
      }
      await runAgent(args[1], args.slice(2));
    } else if (command === 'scan') {
      if (args.length < 2) {
        console.error('Error: URI required for scan command');
        console.log('Usage: seacloud scan "<uri>" [options]');
        process.exit(1);
      }
      await runScan(args[1], args.slice(2));
    } else if (command === 'status') {
      if (args.length < 2) {
        console.error('Error: task ID required for status command');
        console.log('Usage: seacloud status <task-id> [options]');
        process.exit(1);
      }
      await runTaskStatus(args[1], args.slice(2));
    } else if (command === 'app') {
      const subcommand = args[1];
      if (!subcommand) {
        console.error('Error: subcommand required for app command');
        console.log('Usage: seacloud app <subcommand> [options]');
        console.log('Subcommands: search, generation');
        process.exit(1);
      }

      if (subcommand === 'search') {
        if (args.length < 3) {
          console.error('Error: template IDs required for app search command');
          console.log('Usage: seacloud app search "<id1,id2,...>" [options]');
          process.exit(1);
        }
        await runAppSearch(args[2], args.slice(3));
      } else if (subcommand === 'generation') {
        await runAppGeneration(args.slice(2));
      } else {
        console.error(`Error: unknown subcommand '${subcommand}'`);
        console.log('Available subcommands: search, generation');
        process.exit(1);
      }
    } else {
      // Model generation command
      const model = command;

      if (model.startsWith('--')) {
        console.error('Error: command or model name required');
        console.log('Usage: seacloud <command> [options]');
        process.exit(1);
      }

      const options = parseArgs(args.slice(1));
      await testModel(model, options);
    }
  } catch (error: any) {
    console.error('\nError:', error.message);
    process.exit(1);
  }
}

main().catch(console.error);
